import {searchUrl} from '@github-ui/issue-url-helper'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-relay'
import {AssigneeRepositoryPicker} from '@github-ui/item-picker-assignee-relay'
import {formatActorLogin} from '@github-ui/item-picker-assignee-relay/utils/assignee-picker'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import type {ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {MentionIcon, PersonIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import type React from 'react'
import {useCallback, useMemo} from 'react'

import {LABELS} from '../../../constants/labels'
import {useQueryContext, useQueryDebouncedSearch, useQuerySearchState} from '../../../contexts/QueryContext'
import {getTokensByType, replaceTokensDifferentially} from '../../../utils/urls'
import styles from './ListAssigneeFilter.module.css'
import type {FilterBarPickerProps} from './ListItemsHeaderWithoutBulkActions'

export function ListAssigneeFilter({repo: scopedRepo, applySectionFilter, nested}: FilterBarPickerProps) {
  const {name: repo, owner} = scopedRepo
  const {currentViewId} = useQueryContext()
  const {activeSearchQuery} = useQuerySearchState()
  const {debouncedDirtySearchQuery} = useQueryDebouncedSearch()

  const query = debouncedDirtySearchQuery ?? activeSearchQuery

  const currentAssigneeTokens = useMemo(() => {
    const tokens = getTokensByType(query, 'assignee')

    // We want to specifically only show the last assignee token in the filter bar, as this is a single select variant picker and it's unable to show
    // multiple values. This is a workaround until we have a better solution for this.
    // Tracking issue here: https://github.com/github/issues/issues/11863
    return tokens.slice(-1)
  }, [query])

  const noAssigneeOption = useMemo(() => {
    const option: ExtendedItemProps<AssigneePickerAssignee> = {
      id: SPECIAL_VALUES.noAssigneeData.id,
      description: '',
      descriptionVariant: 'inline',
      children: <VerifiedHTMLText html={SPECIAL_VALUES.noAssigneeData.login as SafeHTMLString} />,
      source: SPECIAL_VALUES.noAssigneeData as AssigneePickerAssignee,
      selected: query.includes('no:assignee'),
      leadingVisual: () => <PersonIcon />,
    }
    return option
  }, [query])

  const onSelectionChanged = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      const assignees = selectedAssignees.map(assignee => {
        if (assignee.id === SPECIAL_VALUES.noAssigneeData.id) {
          return 'no:assignee'
        }
        return formatActorLogin(assignee)
      })

      const newQuery = replaceTokensDifferentially(query, assignees, 'assignee')
      const url = searchUrl({viewId: currentViewId, query: newQuery})
      applySectionFilter(newQuery, url)
    },
    [query, applySectionFilter, currentViewId],
  )

  const props = {
    readonly: false,
    title: LABELS.filters.assigneesLabel,
    assigneeTokens: currentAssigneeTokens,
    assignees: [],
    repo,
    owner,
    onSelectionChange: onSelectionChanged,
    noAssigneeOption,
    anchorElement: nested ? NestedAssigneesAnchor : AssigneesAnchor,
    showNoMatchItem: true,
  }

  return (
    <AssigneeRepositoryPicker
      {...props}
      name="assignee"
      selectionVariant="single"
      includeAuthorableBots={false}
      includeAssignableBots
    />
  )
}

function NestedAssigneesAnchor(props: React.HTMLAttributes<HTMLElement>) {
  return (
    <ActionList.Item {...props} aria-label={LABELS.filters.assigneesLabel} role="menuitem">
      <ActionList.LeadingVisual>
        <MentionIcon />
      </ActionList.LeadingVisual>
      {LABELS.filters.assignees}...
    </ActionList.Item>
  )
}

function AssigneesAnchor(props: React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <Button
      variant="invisible"
      data-testid="assignees-anchor-button"
      trailingVisual={TriangleDownIcon}
      aria-label={LABELS.filters.assigneesLabel}
      className={styles.assigneeFilterButton}
      {...props}
    >
      {LABELS.filters.assignees}
    </Button>
  )
}

try{ ListAssigneeFilter.displayName ||= 'ListAssigneeFilter' } catch {}
try{ NestedAssigneesAnchor.displayName ||= 'NestedAssigneesAnchor' } catch {}
try{ AssigneesAnchor.displayName ||= 'AssigneesAnchor' } catch {}